import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Row } from "@tanstack/react-table";
import {
  type Case,
  type Client,
  type DocumentDetail,
  DocumentStatus,
} from "@/briefpoint-client";
import { TableCell, TableRow } from "@/components/ui/table";
import { useDocumentApi } from "@/hooks/useApi";
import {
  getUnarchivedCaseDocumentListOptions,
  getUnarchivedDocumentListOptions,
} from "@/lib/query-options";
import type { DocumentClient, ViewCommunicationResponse } from "@/lib/types";
import { toLocaleUSDateString } from "@/lib/utils";
import DocumentActionsMenu from "../DocumentList/DocumentActionsMenu";
import { EditableText } from "../EditableText/EditableText";
import { UserNotificationIndicator } from "../UserNotificationIndicator";
import { toast } from "../ui/toast";
import { DocumentAction } from "./DocumentAction";
import styles from "./DocumentListTableSupplementalRow.module.scss";

type Props = {
  row: Row<DocumentDetail>; // non-supplemental document
  document: DocumentClient; // supplemental document
  client?: Client;
  case?: Case;
  sendReminder?: () => void;
  setSelectedClient?: (client: Client) => void;
  communication?: ViewCommunicationResponse;
  archived?: boolean;
};

export default function DocumentListTableSupplementalRow(props: Props) {
  const userNotificationCount =
    props.document.interrogatoryNotificationCount ?? 0;
  const documentApi = useDocumentApi();
  const queryClient = useQueryClient();
  const updateDocumentMutation = useMutation({
    mutationFn: async ({
      document,
      name,
    }: {
      document: DocumentClient;
      name: string;
    }) => {
      await documentApi.documentUpdateDocument({
        documentId: document.id,
        document: { ...document, name },
      });
      return { document, name };
    },
    onSuccess: ({ name }: { name: string }) => {
      toast.success(`${name} was updated successfully.`);
    },
    onError: ({ name }: { name: string }) => {
      toast.error(`Error updating ${name}. Please try again later.`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        ...getUnarchivedDocumentListOptions(documentApi),
      });
      if (props.case) {
        queryClient.invalidateQueries({
          ...getUnarchivedCaseDocumentListOptions(documentApi, props.case.id),
        });
      }
    },
  });
  return (
    <TableRow
      key={props.document?.id}
      data-state={props.row.getIsSelected() && "selected"}
    >
      <TableCell colSpan={props.archived ? 4 : 6} className={styles.cellData}>
        <div
          data-testid="supplemental-name-container"
          className={styles.supplementalNameContainer}
        >
          <div className={styles.curveGraphic} />
          <div className={styles.userNotificationContainer}>
            {userNotificationCount > 0 && (
              <UserNotificationIndicator
                count={userNotificationCount}
                isSupplement
              />
            )}
          </div>
          <div
            data-testid="supplemental-name"
            className={styles.supplementalName}
            style={userNotificationCount > 0 ? { right: "8px" } : undefined}
          >
            <EditableText
              value={props.document.name}
              onChange={(name) =>
                updateDocumentMutation.mutate({
                  document: props.document,
                  name,
                })
              }
            />
          </div>
        </div>
      </TableCell>
      <TableCell colSpan={props.archived ? 2 : 4} className={styles.cellData}>
        <div className={styles.supplementalUploadDate}>
          {props.document?.uploadDate
            ? toLocaleUSDateString(props.document?.uploadDate)
            : "-"}
        </div>
      </TableCell>
      <TableCell className={styles.cellData}>
        <div className="d-flex justify-content-end gap-2 align-items-center">
          {!props.archived && (
            <DocumentAction document={props.document as DocumentClient} />
          )}
          {!props.archived &&
            (props.document?.status ?? 0) >= DocumentStatus.Processed && (
              <DocumentActionsMenu
                document={props.document}
                client={props.client}
                _case={props.case}
                sendReminder={props.sendReminder}
                setSelectedClient={props.setSelectedClient}
                communication={props.communication}
              />
            )}
        </div>
      </TableCell>
    </TableRow>
  );
}
